import React, { useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import * as _ from "lodash";
import { format } from "date-fns";
import { Box, HStack, VStack } from "../../components/utils";
import { globalFilter, sort } from "../../utils/arrays";
import BasicMenu from "../../components/shared/Menu";
import { SplitDetail } from "../../types/HistoricDataModel";
import Pagination from "../../components/shared/Pagination";
import { TabTransactionDetail } from "../../types/HistoricOverView";

export default function OverviewSplitTable({
  tabTransactionDetail,
}: TabTransactionDetail) {
  const values =
    tabTransactionDetail?.investorData?.map((element) => ({
      splitNumber: element.splitNumber,
      securityType: element.securityType,
      date: format(new Date(element.date), "yyyy-MM-dd"),
    })) || [];
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [globalFilterText, setGlobalFilterText] = useState("");

  const [sortField, setSortField] = useState<{
    field: keyof SplitDetail | "" | undefined;
    ascending: boolean;
  }>({ field: "date", ascending: false });

  let splitDetailList: SplitDetail[] = values || [];

  splitDetailList = useMemo(() => {
    if (!splitDetailList) return [];
    const filterResult = globalFilter(splitDetailList, globalFilterText, [
      "splitNumber",
      "securityType",
      "date",
    ]);
    const sortResult = sort(
      filterResult,
      sortField?.field,
      sortField?.ascending
    );
    return sortResult;
  }, [splitDetailList, globalFilterText, sortField]);

  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * pageSize;
    const lastPageIndex = firstPageIndex + pageSize;
    return splitDetailList.slice(firstPageIndex, lastPageIndex);
  }, [splitDetailList, currentPage, pageSize]);

  return (
    <Box>
      <HStack aria-label="toolbar" className="justify-between mb-8">
        <VStack className="min-w-max"></VStack>
        <HStack className="h-11 min-w-min">
          <HStack className="items-center p-2 rounded-lg w-72 bg-white text-slate-dark mx-2">
            <Icon icon="fe:search" width="24" className="mr-2 " />
            <input
              type="text"
              className="w-full font-medium border-0 outline-none text-xs2 bg-inherit"
              placeholder="Search"
              value={globalFilterText}
              onChange={(e) => {
                setGlobalFilterText(e.target.value);
              }}
            ></input>
          </HStack>
        </HStack>
      </HStack>
      <Box className="overflow-auto max-h-full">
        <table className="w-full table-space">
          <thead className="text-xs font-medium text-gray-light">
            <tr>
              <td
                className="py-3 w-72 hover:cursor-pointer"
                onClick={() =>
                  setSortField({
                    field: "splitNumber",
                    ascending: !sortField?.ascending,
                  })
                }
              >
                Split Ratio
              </td>
              <td
                className="py-3 w-72 hover:cursor-pointer"
                onClick={() =>
                  setSortField({
                    field: "securityType",
                    ascending: !sortField?.ascending,
                  })
                }
              >
                Security
              </td>
              <td
                className="py-3 hover:cursor-pointer w-72"
                onClick={() =>
                  setSortField({
                    field: "date",
                    ascending: !sortField?.ascending,
                  })
                }
              >
                Date
              </td>
              <td className="py-3 w-56"></td>
            </tr>
          </thead>
          <tbody className={""}>
            {values &&
              currentTableData?.map((splitDetail, index) => (
                <tr key={splitDetail.uuid} className="border-t border-dashed ">
                  <td className="py-4 w-72 align-top ">
                    <HStack>
                      <Box>
                        <p
                          className={`w-72 text-xs font-medium text-gray-dark `}
                        >
                          {splitDetail.splitNumber}
                        </p>
                      </Box>
                    </HStack>
                  </td>
                  <td className="py-4 w-72 align-top ">
                    <HStack className="w-72 ">
                      <Box>
                        <p
                          className={`w-72 text-xs font-medium text-gray-dark`}
                        >
                          {splitDetail.securityType}
                        </p>
                      </Box>
                    </HStack>
                  </td>
                  <td className="py-4 align-top  w-72">
                    <Box>
                      <p className={`w-72 text-xs font-medium text-gray-dark `}>
                        {format(
                          new Date(splitDetail.date || new Date()),
                          "yyyy-MM-dd"
                        )}
                      </p>
                    </Box>
                  </td>
                  <td className="py-3 w-72"></td>
                </tr>
              ))}
          </tbody>
        </table>
        {values.length === 0 && (
          <div className="text-black text-center">
            <h1>No Splits Found</h1>
          </div>
        )}
      </Box>
      <Box className="flex justify-between mt-8">
        <BasicMenu
          defaultValue={pageSize}
          options={[5, 10, 20, 50, 100]}
          onOptionChange={(value) => setPageSize(value)}
        />
        <Pagination
          currentPage={currentPage}
          totalCount={values.length}
          pageSize={pageSize}
          onPageChange={(page: number) => setCurrentPage(page)}
        />
      </Box>
    </Box>
  );
}
